import React from "react";
import { Input, XStack, Text, Label, YStack, TextArea } from "tamagui";
import { colors } from "@styles/colors";
import { useFormContext, Controller } from "react-hook-form";
import SelectItem from "./utils/SelectItem";
import DateItem from "./utils/DateItem";
import { CheckboxItem } from "./utils/CheckboxItem";
import AddressWrapper from "@components/address/AddressWrapper";
import DynamicInputSection from "./utils/DynamicInputSection";
import RadioGroupItem from "./utils/RadioGroupItem";
import { getKeyboardType } from "./func/getKeyboardType";

interface ItemDataProps {
  id: string;
  label: string;
  placeholder: string;
  value?: string | number | boolean;
  otherInfo?: string;
  options?: {
    //-- for select options
    label: string;
    value: string | boolean | number;
  }[];
  statement?: {
    //-- 是否有聲明書閱讀
    need: boolean;
    title?: string;
  };
  defaultValue?: any;
}

interface FormItemProps {
  /** radio 選項改用膠囊外觀（瓦斯訂購 Skin V2）。 */
  pill?: boolean;
  children?: React.ReactNode;
  itemType?: string;
  required: boolean;
  data: ItemDataProps;
  disabled?: boolean;
  /** 選單/輸入變更後回調（可用於連動清除下層欄位） */
  onAfterChange?: (id: string, value: any) => void;
}

const FormItem: React.FC<FormItemProps> = ({ children, itemType, required, data, disabled, onAfterChange, pill }) => {
  const {
    setValue,
    watch,
    control,
    formState: { errors },
  } = useFormContext();

  const errorMessage = (errors[data.id]?.message as string) || undefined;
  const hasError = !!errorMessage;

  return (
    <YStack>
      <XStack justifyContent={"space-between"}>
        {/* label 為空字串且非必填時整個 Label 不渲染：Tamagui 的 Label 即使沒有文字仍佔一整列高度，
            結帳頁的欄位標題是由外層 GasLabel 畫的（data.label 一律留空），會多出一道看得見的空隙。 */}
        {itemType !== "checkbox" && (required || !!data.label) && (
          <Label htmlFor={data.id} fontWeight="bold" fontSize={14}>
            {/* "必填"標籤 */}
            {required && (
              <Text color={colors.darkRed} fontWeight="700" fontSize={14} mr={8}>
                *
              </Text>
            )}
            {data.label}
          </Label>
        )}
      </XStack>
      <Controller
        control={control}
        name={data.id}
        defaultValue={data.value}
        render={({ field: { onChange, onBlur, value } }) => (
          <>
            {itemType === "input" && (
              <Input
                disabled={disabled}
                id={data.id}
                placeholder={data?.placeholder || "請輸入資料"}
                value={value?.toString() || ""}
                onChangeText={(text: string) => {
                  onChange(text);
                  onAfterChange?.(data.id, text ?? "");
                }}
                onBlur={onBlur}
                inputMode={(getKeyboardType(data?.id ?? "") || undefined) as React.InputHTMLAttributes<HTMLInputElement>["inputMode"]}
                backgroundColor="#ffffff"
                color="#111827"
                placeholderTextColor="#9aa0ab"
                borderColor={hasError ? "$red10" : "#e1e1e1"}
              />
            )}
            {itemType === "textArea" && (
              <TextArea
                disabled={disabled}
                id={data.id}
                placeholder={data?.placeholder || "請輸入資料"}
                value={value?.toString() || ""}
                onChangeText={(text: string) => {
                  onChange(text);
                  onAfterChange?.(data.id, text ?? "");
                }}
                onBlur={onBlur}
                inputMode={(getKeyboardType(data?.id ?? "") || undefined) as React.InputHTMLAttributes<HTMLTextAreaElement>["inputMode"]}
                backgroundColor="#ffffff"
                color="#111827"
                placeholderTextColor="#9aa0ab"
                borderColor={hasError ? "$red10" : "#e1e1e1"}
              />
            )}
            {itemType === "dynamicInputSection" && (
              <DynamicInputSection
                id={data.id}
                placeholder={data?.placeholder}
                addButtonText={`新增${data?.label}`}
                parentError={errors?.[data.id]}
                defaultValue={data?.defaultValue}
              />
            )}
            {itemType === "select" && (
              <SelectItem
                data={data}
                value={value?.toString() || ""}
                hasError={hasError}
                disabled={disabled}
                onChange={(v) => {
                  onChange(v);
                  onAfterChange?.(data.id, v ?? "");
                }}
              />
            )}
            {itemType === "date" && <DateItem data={data} hasError={hasError} onChange={onChange} value={value} />}
            {itemType === "checkbox" && <CheckboxItem data={data} value={value} onChange={onChange} />}
            {itemType === "radio" && <RadioGroupItem data={data} value={value} onChange={onChange} hasError={!!errors[data?.id]} disabled={false} pill={pill} />}
            {itemType === "address" && <AddressWrapper setValue={setValue} watch={watch} parentError={errors?.[data.id]} />}
            {!itemType && children}
          </>
        )}
      />

      {/* ERROR TEXT */}
      {hasError && (
        <Text color="$red10" fontSize={12}>
          {errorMessage}
        </Text>
      )}
    </YStack>
  );
};

export default FormItem;
